import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { Subscription } from 'rxjs';

import {
    languageService,
    languageSupport,
} from '@unpispas/upp-base';
import type { Pinpad, PlaceView, TicketView } from '@unpispas/upp-data';
import { paytefService } from '@unpispas/feature/drivers';
import type { PinpadOutcome } from '@unpispas/feature/drivers';
import { UppWdgtModule } from '@unpispas/upp-wdgt';

type CardTermDialog = 'mark-paid' | 'mark-unpaid' | 'unreachable';

/**
 * Pending card-terminal unlock for a ticket: banner, unlock menu, and
 * reconcile-on-open. Hosts commit PAYCARD only via {@link OnApplied}.
 */
@Component({
    selector: 'upp-pinpad-ticket',
    standalone: true,
    imports: [
        CommonModule,
        IonicModule,
        UppWdgtModule,
    ],
    templateUrl: './upp-ticket.html',
    styleUrls: ['./upp-ticket.scss'],
})
export class UppPinpadTicketComponent extends languageSupport implements OnChanges, OnDestroy {
    @Input() ticket: TicketView | null = null;
    @Input() place: PlaceView | null = null;

    /** Emitted when {@link TicketView.ApplyPinpad} succeeds and the host must commit PAYCARD. */
    @Output() OnApplied = new EventEmitter<void>();

    /** Emitted when unlock mutates the ticket without a sale apply/commit. */
    @Output() OnMutated = new EventEmitter<void>();

    /** Persistent / transient notice for card-terminal reconcile (`@ticket_cardterm_*`). */
    CardTermNotice: string | null = null;

    /**
     * Preloaded confirm / error panel for pending-card unlock
     * (`mark-paid` | `mark-unpaid` | `unreachable`).
     */
    CardTermDialog: CardTermDialog | null = null;

    /** Bumps on ticket change / destroy so in-flight reconcile aborts. */
    private _reconcileGen = 0;

    private _cardTermBusy = false;

    /**
     * Unlock kind captured when opening mark-paid / mark-unpaid confirm.
     * The persistent notice may change (reconcile) while the dialog is open.
     */
    private _cardTermDialogKind: 'SA' | 'RE' | null = null;

    /** Keeps the banner in sync when PE appears on the open edit copy (same ticket @Input). */
    private _ticketDataSub: Subscription | null = null;
    private _ticketViewSub: Subscription | null = null;

    constructor(
        lang: languageService,
        private change: ChangeDetectorRef,
        private readonly paytef: paytefService,
    ) {
        super(lang, change);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ticket'] || changes['place']) {
            this.CardTermDialog = null;
            this._cardTermDialogKind = null;
            this._BindTicketRefresh(this.ticket);
            void this._ReconcileCard();
        }
    }

    ngOnDestroy(): void {
        this._reconcileGen++;
        this._UnbindTicketRefresh();
        this.OnDestroy();
    }

    /**
     * Re-reads pending / refund notice from the current ticket without querying the terminal.
     * Used after a charge dialog closes with an unresolved `PE` on the same open copy.
     */
    RefreshNotice(): void {
        this._SyncCardTermNotice();
        this.change.markForCheck();
    }

    /** True when the notice is a pending PE unlock (sale or refund). */
    get CanUnlockCardTerm(): boolean {
        return this.CardTermUnlockKind !== null;
    }

    /** Which pending attempt the unlock menu targets. */
    get CardTermUnlockKind(): 'SA' | 'RE' | null {
        if (this.CardTermNotice === '@ticket_cardterm_pending_ticket') {
            return 'SA';
        }
        if (this.CardTermNotice === '@ticket_cardterm_refund_pending_ticket') {
            return 'RE';
        }
        return null;
    }

    /** Subtitle for the persistent card-terminal warning banner (title + hint). */
    get CardTermSubtitle(): string | null {
        if (this.CardTermNotice === '@ticket_cardterm_pending_ticket') {
            return '@ticket_cardterm_pending_hint';
        }
        if (this.CardTermNotice === '@ticket_cardterm_refund_pending_ticket') {
            return '@ticket_cardterm_refund_pending_hint';
        }
        return null;
    }

    get CardTermDialogTitle(): string {
        const _kind = this._cardTermDialogKind ?? this.CardTermUnlockKind;
        if (this.CardTermDialog === 'mark-paid') {
            return _kind === 'RE'
                ? '@ticket_cardterm_unlock_paid_refund_confirm_title'
                : '@ticket_cardterm_unlock_paid_confirm_title';
        }
        if (this.CardTermDialog === 'mark-unpaid') {
            return _kind === 'RE'
                ? '@ticket_cardterm_unlock_unpaid_refund_confirm_title'
                : '@ticket_cardterm_unlock_unpaid_confirm_title';
        }
        return '@ticket_cardterm_unlock_unreachable_title';
    }

    get CardTermDialogMessage(): string {
        const _kind = this._cardTermDialogKind ?? this.CardTermUnlockKind;
        if (this.CardTermDialog === 'mark-paid') {
            return _kind === 'RE'
                ? '@ticket_cardterm_unlock_paid_refund_confirm_message'
                : '@ticket_cardterm_unlock_paid_confirm_message';
        }
        if (this.CardTermDialog === 'mark-unpaid') {
            return _kind === 'RE'
                ? '@ticket_cardterm_unlock_unpaid_refund_confirm_message'
                : '@ticket_cardterm_unlock_unpaid_confirm_message';
        }
        return '@ticket_cardterm_unlock_unreachable';
    }

    /******** pending unlock ********/

    OnCardTermAskMarkPaid(): void {
        const _kind = this.CardTermUnlockKind;
        if (!_kind || this._cardTermBusy) {
            return;
        }
        this._cardTermDialogKind = _kind;
        this.CardTermDialog = 'mark-paid';
        this.change.markForCheck();
    }

    OnCardTermAskMarkUnpaid(): void {
        const _kind = this.CardTermUnlockKind;
        if (!_kind || this._cardTermBusy) {
            return;
        }
        this._cardTermDialogKind = _kind;
        this.CardTermDialog = 'mark-unpaid';
        this.change.markForCheck();
    }

    OnCardTermDialogClose(): void {
        this.CardTermDialog = null;
        this._cardTermDialogKind = null;
        this.change.markForCheck();
    }

    async OnCardTermDialogAccept(): Promise<void> {
        const _dialog = this.CardTermDialog;
        const _kind = this._cardTermDialogKind;
        this.CardTermDialog = null;
        this._cardTermDialogKind = null;
        this.change.markForCheck();

        if (_dialog === 'mark-paid') {
            await this._CardTermMarkPaid(_kind);
            return;
        }
        if (_dialog === 'mark-unpaid') {
            await this._CardTermMarkUnpaid(_kind);
        }
    }

    async OnCardTermCheck(): Promise<void> {
        const _kind = this.CardTermUnlockKind;
        const _ticket = this.ticket;
        const _place = this.place;
        if (!_kind || !_ticket || !_place || this._cardTermBusy) {
            return;
        }

        this._cardTermBusy = true;
        this.change.markForCheck();

        try {
            const _terminal = this._CardTermTerminal(_kind);
            if (!_terminal) {
                this.CardTermDialog = 'unreachable';
                return;
            }

            this.CardTermNotice = _kind === 'RE'
                ? '@ticket_cardterm_refund_checking'
                : '@ticket_cardterm_checking';
            this.change.markForCheck();

            const _outcome = await this.paytef.Resolve([_ticket], _terminal, _kind);

            // Only close from Consultar when Resolve confirmed approval — never apply a
            // stale AP/MA after UNKNOWN/DENIED (that would pay without a terminal answer).
            if (_kind === 'SA' && _outcome === 'APPROVED' && _ticket.PinpadToApply) {
                if (await _ticket.ApplyPinpad()) {
                    this.OnApplied.emit();
                }
            }

            this._SyncCardTermNotice();

            const _stillPe = _kind === 'RE'
                ? _ticket.RefundPending?.status === 'PE'
                : _ticket.PinpadPending?.status === 'PE';
            if (_outcome === 'UNKNOWN' && _stillPe) {
                this.CardTermDialog = 'unreachable';
            }
        }
        finally {
            this._cardTermBusy = false;
            this.change.markForCheck();
        }
    }

    private async _CardTermMarkPaid(kind: 'SA' | 'RE' | null): Promise<void> {
        const _kind = kind;
        const _ticket = this.ticket;
        if (!_kind || !_ticket || this._cardTermBusy) {
            return;
        }

        // Converting an existing PE/MM only needs the row; a terminal is required
        // only when DeclareManual must create a new MA attempt.
        const _hasPending = _kind === 'RE'
            ? !!_ticket.RefundPending
            : !!_ticket.PinpadPending;
        const _terminal = this._CardTermTerminal(_kind);
        if (!_terminal && !_hasPending) {
            this.CardTermDialog = 'unreachable';
            this.change.markForCheck();
            return;
        }

        this._cardTermBusy = true;
        this.change.markForCheck();
        try {
            await this.paytef.DeclareManual([_ticket], _terminal, _kind);
            // Banner clears from local MA/AP state before host commit / network flush.
            this._SyncCardTermNotice();
            if (_kind === 'SA' && _ticket.PinpadToApply) {
                if (await _ticket.ApplyPinpad()) {
                    this.OnApplied.emit();
                }
                else {
                    this.OnMutated.emit();
                }
            }
            else {
                this.OnMutated.emit();
            }
            this._SyncCardTermNotice();
        }
        finally {
            this._cardTermBusy = false;
            this.change.markForCheck();
        }
    }

    private async _CardTermMarkUnpaid(kind: 'SA' | 'RE' | null): Promise<void> {
        const _kind = kind;
        const _ticket = this.ticket;
        if (!_kind || !_ticket || this._cardTermBusy) {
            return;
        }

        this._cardTermBusy = true;
        this.change.markForCheck();
        try {
            await this.paytef.Supersede([_ticket], _kind);
            this.OnMutated.emit();
            this._SyncCardTermNotice();
        }
        finally {
            this._cardTermBusy = false;
            this.change.markForCheck();
        }
    }

    private _CardTermTerminal(kind: 'SA' | 'RE'): Pinpad | null {
        const _ticket = this.ticket;
        const _place = this.place;
        if (!_ticket || !_place) {
            return null;
        }

        if (kind === 'RE') {
            return (_ticket.RefundPending?.pinpad as Pinpad | null) ?? _place.OwnPinpad;
        }

        return (_ticket.PinpadPending?.pinpad as Pinpad | null) ?? _place.OwnPinpad;
    }

    private _SyncCardTermNotice(): void {
        const _ticket = this.ticket;
        if (!_ticket) {
            this.CardTermNotice = null;
            return;
        }

        // No automatic refund registration here: selection/units are not kept.
        if (_ticket.PinpadPending) {
            this.CardTermNotice = '@ticket_cardterm_pending_ticket';
        }
        else if (_ticket.RefundPending) {
            this.CardTermNotice = '@ticket_cardterm_refund_pending_ticket';
        }
        else if (_ticket.RefundToApply) {
            this.CardTermNotice = '@ticket_refund_unregistered';
        }
        else {
            this.CardTermNotice = null;
        }
    }

    private _BindTicketRefresh(ticket: TicketView | null): void {
        this._UnbindTicketRefresh();
        if (!ticket?.OnDataRefresh || !ticket?.OnViewRefresh) {
            return;
        }

        this._ticketDataSub = ticket.OnDataRefresh.subscribe(() => {
            this.RefreshNotice();
        });
        this._ticketViewSub = ticket.OnViewRefresh.subscribe(() => {
            this.RefreshNotice();
        });
    }

    private _UnbindTicketRefresh(): void {
        this._ticketDataSub?.unsubscribe();
        this._ticketDataSub = null;
        this._ticketViewSub?.unsubscribe();
        this._ticketViewSub = null;
    }

    /******** reconcile ********/

    private async _ReconcileCard(): Promise<void> {
        const _ticket = this.ticket;
        const _place = this.place;
        if (!_ticket || !_place || _place.CardMethod !== 'TERMINAL') {
            this.CardTermNotice = null;
            return;
        }

        const _gen = ++this._reconcileGen;

        try {
            const _pending = _ticket.PinpadPending;
            let _saleOutcome: PinpadOutcome | null = null;
            if (_pending?.status === 'PE') {
                const _terminal = (_pending.pinpad as Pinpad | null) ?? _place.OwnPinpad;
                if (_terminal) {
                    this.CardTermNotice = '@ticket_cardterm_checking';
                    this.change.markForCheck();
                    _saleOutcome = await this.paytef.Resolve([_ticket], _terminal);
                    if (_gen !== this._reconcileGen) {
                        return;
                    }
                }
            }

            // Apply only a pre-existing AP/MA, or one Resolve just confirmed — never a
            // stale AP/MA after UNKNOWN/DENIED while PE is still open (same as Consultar).
            if (
                _ticket.PinpadToApply &&
                (_saleOutcome === null || _saleOutcome === 'APPROVED')
            ) {
                if (await _ticket.ApplyPinpad()) {
                    if (_gen !== this._reconcileGen) {
                        return;
                    }
                    this.OnApplied.emit();
                }
                if (_gen !== this._reconcileGen) {
                    return;
                }
            }

            const _refundPending = _ticket.RefundPending;
            if (_refundPending?.status === 'PE') {
                const _refundTerminal = (_refundPending.pinpad as Pinpad | null) ?? _place.OwnPinpad;
                if (_refundTerminal) {
                    this.CardTermNotice = '@ticket_cardterm_refund_checking';
                    this.change.markForCheck();
                    await this.paytef.Resolve([_ticket], _refundTerminal, 'RE');
                    if (_gen !== this._reconcileGen) {
                        return;
                    }
                }
            }

            this._SyncCardTermNotice();
        }
        finally {
            if (_gen === this._reconcileGen) {
                this.change.markForCheck();
            }
        }
    }
}
